import { Autocomplete, Box, FormControl, Icon, TextField, Typography } from "@mui/material";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SubmitButton from "../UI/SubmitButton";
import { useEffect, useState, useCallback } from "react";
import EntryButton from "../UI/EntryButton";
import { useDispatch, useSelector } from "react-redux";
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import { clearFilterVendor, setSelectFilter } from "../store/vendorSlice";
import { Link } from "react-router-dom";
import { setModal } from "../store/modalSlice";
import { useTranslation } from "react-i18next";
import { wasteProductsAction } from "../asyncActions/warhouseActions";
import { addWasteDocumentData, clearData, removeWasteDocumentData, setWasteDocumentData } from "../store/gridDataSlice";

function WasteProductPage() {
  const { t } = useTranslation();

  const apiRef = useGridApiRef();
  const dispatch = useDispatch();

  const { productList, selectFilter } = useSelector((store) => store.vendor);
  const { wasteDocumentData } = useSelector((store) => store.gridData);
  const { role } = useSelector((store) => store.authorization);

  const [error, setError] = useState(null);

  // Локальные стейты выбранных значений двух селектов
  const [selectedProduct, setSelectedProduct] = useState(null);      // из productsOption (id_s2s === 1001)
  const [selectedIngredient, setSelectedIngredient] = useState(null); // из productsIngradientOption (id_s2s !== 1001)

  const rows = wasteDocumentData;

  const columns = [
    {
      field: "action",
      type: "actions",
      headerName: t("grid.action"),
      maxWidth: 80,
      renderCell: (params) => {
        const id = params.row.id;
        return (
          <Box>
            <Icon
              component={DeleteIcon}
              onClick={() => {
                dispatch(removeWasteDocumentData(id));
              }}
            />
          </Box>
        );
      },
    },
    { field: "id", headerName: "id", maxWidth: 80 },
    { field: "name", headerName: t("grid.product_name"), minWidth: 150, flex: 1 },
    { field: "unit_type", headerName: t("grid.unit_type"), maxWidth: 80, flex: 1 },
    {
      field: "quantity",
      headerName: t("grid.qnt"),
      editable: true,
      type: "number",
      minWidth: 80,
      flex: 1,
    },
  ];

  function addHandle(e) {
    e.preventDefault();

    // Проверка на одновременный выбор двух продуктов
    if (selectedProduct && selectedIngredient) {
      setError("Нельзя выбрать два продукта одновременно");
      return;
    }

    const selected = selectedProduct || selectedIngredient;

    if (!selected) {
      setError("Product required");
      return;
    }

    const findProduct = productList.find((elem) => elem.id === selected.id);
    if (findProduct) {
      if (!rows.some((el) => el.id === findProduct.id)) {
        dispatch(addWasteDocumentData({ ...findProduct }));
        setError(null);

        // Очистка выбора в обоих селектах после успешного добавления
        setSelectedProduct(null);
        setSelectedIngredient(null);
      } else {
        setError("Product already exist");
      }
    } else {
      setError("Product required");
    }
  }

  function onEdit(row) {
    dispatch(setWasteDocumentData(row));
    return row;
  }

  function onSubmit() {
    if (wasteDocumentData.every((elem) => elem.quantity > 0)) {
      dispatch(setModal({ active: true, content: "wasteDocument" }));
      setError(null);
    } else {
      setError("Products with 0 quantity not allowed");
    }
  }

  function setClear() {
    dispatch(clearData("wasteDocumentData"));
    dispatch(clearData("product"));
    // Сброс локальных селектов
    setSelectedProduct(null);
    setSelectedIngredient(null);
    setError(null);
  }

  useEffect(() => {
    dispatch(wasteProductsAction());
    return () => setClear();
  }, [dispatch]);

  const handleCellClick = useCallback(
    (params) => {
      try {
        apiRef.current.startCellEditMode({
          id: params.id,
          field: params.field,
        });
      } catch (e) {}
    },
    [apiRef]
  );

  const productsIngradientOption = productList
    .filter((el) => el.id_s2s !== 1001)
    .map((el) => ({ label: el.name, id: el.id }));

  const productsOption = productList
    .filter((el) => el.id_s2s === 1001)
    .map((el) => ({ label: el.name, id: el.id }));

  // Если вам по-прежнему нужен selectFilter.product для чего-то в сторе — можно синхронизировать:
  // но для логики добавления используем локальные selectedProduct/selectedIngredient.
  const syncSelectFilter = (value) => {
    dispatch(setSelectFilter({ filter: "product", value }));
  };

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 4, height: "100%" }}>
        <Typography variant="h4" align="center">
          Waste products
        </Typography>

        {error && (
          <Typography sx={{ color: "red" }} align="center">
            {error}
          </Typography>
        )}

        <Box
          sx={{ display: "flex", gap: 5, justifyContent: "center" }}
          component="form"
          onSubmit={addHandle}
        >
          {/* Первый селект (продукты с id_s2s === 1001) */}
          <FormControl disabled={!selectFilter.vendor} sx={{ width: "250px" }}>
            <Autocomplete
              disablePortal
              value={selectedProduct}
              options={productsOption}
              onChange={(_, value) => {
                setSelectedProduct(value);
                // взаимное обнуление второго при выборе первого
                if (value) setSelectedIngredient(null);
                setError(null);
                syncSelectFilter(value);
              }}
              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
              sx={{ width: "250px" }}
              renderInput={(params) => (
                <TextField disabled={!selectFilter.vendor} {...params} label="Վաճառքի ապրանքներ" />
              )}
            />
          </FormControl>

          {/* Второй селект (ингредиенты с id_s2s !== 1001) */}
          <FormControl disabled={!selectFilter.vendor} sx={{ width: "250px" }}>
            <Autocomplete
              disablePortal
              value={selectedIngredient}
              options={productsIngradientOption}
              onChange={(_, value) => {
                setSelectedIngredient(value);
                // взаимное обнуление первого при выборе второго
                if (value) setSelectedProduct(null);
                setError(null);
                syncSelectFilter(value);
              }}
              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
              sx={{ width: "250px" }}
              renderInput={(params) => (
                <TextField disabled={!selectFilter.vendor} {...params} label="Բաղադրիչներ" />
              )}
            />
          </FormControl>

          <SubmitButton endIcon={<AddIcon />} type="submit">
            {t("button.add")}
          </SubmitButton>
        </Box>

        <Box sx={{ height: "100%" }}>
          <DataGrid
            getRowId={(row) => row.id}
            apiRef={apiRef}
            rows={rows}
            onCellClick={handleCellClick}
            columns={columns}
            processRowUpdate={onEdit}
            onProcessRowUpdateError={(e) => console.log(e)}
            disableRowSelectionOnClick
            sx={{ minHeight: "150px" }}
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <EntryButton disabled={!rows.length} endIcon={<DeleteIcon />} onClick={setClear}>
            {t("button.clear")}
          </EntryButton>
          <Box sx={{ display: "flex", gap: "10px" }}>
            <Link to="/waste/list">
              <SubmitButton>{t("button.all_waste")}</SubmitButton>
            </Link>
            <SubmitButton disabled={!rows.length} endIcon={<SendIcon />} onClick={onSubmit}>
              {t("button.submit")}
            </SubmitButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default WasteProductPage;
